/**
 * OTPInput Component
 * 
 * A stable, reusable OTP input component with 4 boxes
 * Supports: typing, backspace, paste, auto-focus, mobile keyboards
 */

import { useRef, useEffect } from 'react';

const OTPInput = ({ value = "", onChange, disabled = false, autoFocus = true }) => {
  // Create refs for all 4 inputs
  const inputRefs = useRef([]);

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 4);
  }, []);

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  // Convert string value to array for easier manipulation
  const getOTPArray = () => {
    const arr = value.split('');
    return [
      arr[0] || '',
      arr[1] || '',
      arr[2] || '',
      arr[3] || ''
    ];
  };

  const otpArray = getOTPArray();

  // Handle input change
  const handleChange = (index, e) => {
    const inputValue = e.target.value;
    
    // Only allow single digit
    if (inputValue.length > 1) {
      return;
    }

    // Only allow numbers
    if (inputValue && !/^\d$/.test(inputValue)) {
      return;
    }

    // Update OTP value
    const newOtpArray = [...otpArray];
    newOtpArray[index] = inputValue;
    const newOtp = newOtpArray.join('');
    
    onChange(newOtp);

    // Auto-focus next input if digit was entered
    if (inputValue && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle key down events
  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      e.preventDefault();
      
      const newOtpArray = [...otpArray];
      
      if (otpArray[index]) {
        // Clear current digit
        newOtpArray[index] = '';
        onChange(newOtpArray.join(''));
      } else if (index > 0) {
        // Move to previous input and clear it
        newOtpArray[index - 1] = '';
        onChange(newOtpArray.join(''));
        inputRefs.current[index - 1]?.focus();
      }
    }
    
    // Handle left arrow
    else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }
    
    // Handle right arrow
    else if (e.key === 'ArrowRight' && index < 3) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    
    const pastedData = e.clipboardData.getData('text/plain').trim();
    
    // Only process if it's exactly 4 digits
    if (/^\d{4}$/.test(pastedData)) {
      onChange(pastedData);
      // Focus last input
      inputRefs.current[3]?.focus();
    }
  };

  // Handle focus - select all text
  const handleFocus = (e) => {
    e.target.select();
  };

  return (
    <div style={styles.container}>
      {[0, 1, 2, 3].map((index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="tel"
          inputMode="numeric"
          maxLength={1}
          value={otpArray[index]}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={handleFocus}
          disabled={disabled}
          style={{
            ...styles.input,
            ...(disabled ? styles.inputDisabled : {})
          }}
          autoComplete="off"
          aria-label={`OTP digit ${index + 1}`}
        />
      ))}
    </div>
  );
};

// Styles
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '24px'
  },
  input: {
    width: '56px',
    height: '56px',
    textAlign: 'center',
    fontSize: '20px',
    fontWeight: '600',
    backgroundColor: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: "'Inter', sans-serif",
    cursor: 'text'
  },
  inputDisabled: {
    backgroundColor: '#f3f4f6',
    cursor: 'not-allowed',
    opacity: 0.6
  }
};

export default OTPInput;
